import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Clock } from 'lucide-react';
import { useSessions } from '../../hooks/useSessions';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { SessionCard } from '../../components/beachvision/SessionCard';
import { LoadingSkeleton } from '../../components/beachvision/LoadingSkeleton';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';

function HistoryInner() {
  const { data: sessions, isLoading, isError, refetch } = useSessions();

  const chartData = useMemo(() => {
    return [...(sessions || [])]
      .filter((s) => s.status === 'complete' && s.overall_score != null)
      .sort(
        (a, b) =>
          new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
      )
      .map((s) => ({
        label: new Date(s.recorded_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        score: Number(s.overall_score),
      }));
  }, [sessions]);

  return (
    <>
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <Clock className="h-5 w-5 text-beachvision-coral" />
        <h1 className="font-display font-black text-xl tracking-tight text-beachvision-primary-light">
          History
        </h1>
      </header>

      {isLoading ? (
        <div className="px-4 space-y-4">
          <LoadingSkeleton className="h-36 w-full" />
          <LoadingSkeleton className="h-24 w-full" />
          <LoadingSkeleton className="h-24 w-full" />
        </div>
      ) : isError ? (
        <div className="px-4 text-center py-12">
          <p className="text-beachvision-muted mb-4">Could not load sessions.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3 font-display font-bold"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Score-over-time chart */}
          {chartData.length > 1 ? (
            <section className="px-4 mb-6">
              <p className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide mb-3">
                Score over time
              </p>
              <div className="rounded-2xl bg-beachvision-surface-low p-4">
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: '#8892a4', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fill: '#8892a4', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#232a38',
                        border: 'none',
                        borderRadius: 8,
                        color: '#fff',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#FF6B6B"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: '#FF6B6B', strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          ) : chartData.length === 1 ? (
            <section className="px-4 mb-6">
              <div className="rounded-2xl bg-beachvision-surface-low p-4 text-center">
                <p className="text-beachvision-muted text-sm">
                  Complete more sessions to see your score trend.
                </p>
              </div>
            </section>
          ) : null}

          {/* All sessions */}
          <section className="px-4">
            <p className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide mb-3">
              All sessions
              {sessions && sessions.length > 0 && (
                <span className="ml-2 text-beachvision-coral">{sessions.length}</span>
              )}
            </p>
            {!sessions || sessions.length === 0 ? (
              <div className="rounded-2xl bg-beachvision-surface-low p-8 text-center">
                <div className="text-5xl mb-3" aria-hidden>
                  🏐
                </div>
                <p className="text-white font-display font-semibold mb-1">
                  No sessions yet
                </p>
                <p className="text-beachvision-muted text-sm">
                  Upload your first video from the Analysis tab.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map((s) => (
                  <SessionCard key={s.id} {...s} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

export default function HistoryPage() {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <HistoryInner />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
